import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'
import { runDeploymentChecks, generateDeploymentReport } from '../utils/deploymentChecks'

const { FiCheckCircle, FiAlertTriangle, FiXCircle, FiDownload, FiExternalLink } = FiIcons

const DeploymentStatus = () => {
  const [deploymentStatus, setDeploymentStatus] = useState(null)
  const [showReport, setShowReport] = useState(false)

  useEffect(() => {
    setDeploymentStatus(runDeploymentChecks())
  }, [])

  const downloadReport = () => {
    const report = generateDeploymentReport()
    const blob = new Blob([report], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `aiximo-deployment-report-${new Date().toISOString().split('T')[0]}.md`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  if (!deploymentStatus) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    )
  }

  const { checks, readinessScore, isReady } = deploymentStatus

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pass': return FiCheckCircle
      case 'warning': return FiAlertTriangle
      case 'fail': return FiXCircle
      default: return FiAlertTriangle
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'pass': return 'text-green-600'
      case 'warning': return 'text-yellow-600'
      case 'fail': return 'text-red-600'
      default: return 'text-gray-600'
    }
  }

  return (
    <div className="space-y-6">
      {/* Overall Status */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`p-6 rounded-xl border-2 ${
          isReady 
            ? 'bg-green-50 border-green-200' 
            : 'bg-yellow-50 border-yellow-200'
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <SafeIcon 
              icon={isReady ? FiCheckCircle : FiAlertTriangle} 
              className={`h-8 w-8 ${isReady ? 'text-green-600' : 'text-yellow-600'}`} 
            />
            <div>
              <h3 className={`text-xl font-bold ${isReady ? 'text-green-900' : 'text-yellow-900'}`}>
                {isReady ? 'Ready for Deployment!' : 'Almost Ready'}
              </h3>
              <p className={`${isReady ? 'text-green-700' : 'text-yellow-700'}`}>
                Readiness Score: {readinessScore}%
              </p>
            </div>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setShowReport(!showReport)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {showReport ? 'Hide Details' : 'Show Details'}
            </button>
            <button
              onClick={downloadReport}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center space-x-2"
            >
              <SafeIcon icon={FiDownload} className="h-4 w-4" />
              <span>Report</span>
            </button>
          </div>
        </div>
      </motion.div>

      {/* Progress Bar */}
      <div className="w-full bg-gray-200 rounded-full h-3">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${readinessScore}%` }}
          transition={{ duration: 1, ease: "easeOut" }}
          className={`h-3 rounded-full ${
            readinessScore >= 90 ? 'bg-green-600' : 
            readinessScore >= 70 ? 'bg-yellow-600' : 'bg-red-600'
          }`}
        />
      </div>

      {/* Detailed Checks */}
      {showReport && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          transition={{ duration: 0.3 }}
          className="space-y-3"
        >
          {checks.map((check, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200"
            >
              <div className="flex items-center space-x-3">
                <SafeIcon 
                  icon={getStatusIcon(check.status)} 
                  className={`h-5 w-5 ${getStatusColor(check.status)}`} 
                />
                <div>
                  <h4 className="font-medium text-gray-900">{check.name}</h4>
                  <p className="text-sm text-gray-600">{check.message}</p>
                </div>
              </div>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                check.status === 'pass' ? 'bg-green-100 text-green-800' :
                check.status === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>
                {check.status.toUpperCase()}
              </span>
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Deployment Actions */}
      {isReady && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-6 bg-blue-50 rounded-xl border border-blue-200"
        >
          <h3 className="text-lg font-bold text-blue-900 mb-4">Ready to Deploy!</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <a
              href="https://vercel.com/new"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 px-4 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
            >
              <SafeIcon icon={FiExternalLink} className="h-4 w-4" />
              <span>Deploy to Vercel</span>
            </a>
            <a
              href="https://app.netlify.com/start"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 px-4 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
            >
              <SafeIcon icon={FiExternalLink} className="h-4 w-4" />
              <span>Deploy to Netlify</span>
            </a>
          </div>
          <p className="text-blue-700 text-sm mt-4">
            Both platforms offer free hosting with automatic SSL and global CDN.
          </p>
        </motion.div>
      )}
    </div>
  )
}

export default DeploymentStatus